import React, { useEffect, useLayoutEffect, useState } from "react";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import { makeStyles } from "@mui/styles";

import ProductService from "../../service/product";
import CommonLayout from "../Common/Layout";

const useStyles = makeStyles({
  subContainer: {
    display: "flex",
    flexDirection: "column",
    padding: 0,
    borderTop: 0,
  },
  PanelContainer: {
    padding: "0 30px",
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  Panel: {
    padding: "25px",
    backgroundColor: "#FFFFFF",
    boxShadow: "0 3px 10px rgb(0 0 0 / 0.2)",
    marginTop: 0,
    borderRadius: 10,
  },
  table: {
    "& .MuiTableCell-root": {
      borderLeft: "1px solid darkgray",
    },
  },
  ZoomedImageDiv: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
  },
  ZoomedImage: {
    cursor: "zoom-out",
    maxWidth: "100%",
    maxHeight: "90%",
    width: "auto",
    height: "auto",
    margin: "auto",
    transition: "all 0.5s ease-in-out",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
  productCategoryBtn: {
    backgroundColor: "#338cf0 !important",
    color: "#ffffff !important",
  },
  unselectedCategory: {
    backgroundColor: "#ffffff !important",
    color: "#338cf0 !important",
  },
});

const Products = () => {
  const classes = useStyles();

  const [productsData, setProductsData] = useState([]);
  const [productsPriceListData, setProductsPriceListData] = useState([]);
  const [productCategories, setProductCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableHeight, setTableHeight] = useState(0);
  const [zoomedIndex, setZoomedIndex] = useState(null);
  const [selectedProductCategory, setSelectedProductCategory] = useState(0);

  // function to toggle zoom state
  const toggleZoom = (index) => {
    setZoomedIndex(index === zoomedIndex ? null : index);
  };

  // Load Products list
  useEffect(() => {
    (async () => {
      setLoading(true);

      const productService = new ProductService();
      const prodDataResponse = await productService.getAllProducts({
        limit: 500,
      });

      if (prodDataResponse.success) {
        setProductsData(prodDataResponse.data.productList);
      }

      const priceListResponse = await productService.getProductsPriceList({});

      if (priceListResponse.success) {
        setProductsPriceListData(priceListResponse.data);
      }

      const prodCategoriesResponse = await productService.getProductCategories();

      if (prodCategoriesResponse.success) {
        if (prodCategoriesResponse.data.length > 0) {
          setProductCategories(prodCategoriesResponse.data);
        }
      }

      setLoading(false);
    })();
  }, []);

  useLayoutEffect(() => {
    function updateSize() {
      setTableHeight(window.innerHeight - 249);
    }
    window.addEventListener("resize", updateSize);
    updateSize();

    return () => window.removeEventListener("resize", updateSize);
  }, []);

  const handleCategoryClick = (categoryId) => {
    setSelectedProductCategory(categoryId);
  };

  const breadcrumbArr = [{ link: "#", displayName: "Products", active: true }];

  // Filter product data according to the selected category
  const filteredProducts = productsData.filter((prod) => prod.Category_id === selectedProductCategory);

  return (
    <CommonLayout title="Products" breadcrumbArr={breadcrumbArr}>
      <Box
        component="main"
        sx={{
          backgroundColor: (theme) => {
            return theme.palette.TiffanyBlue.main;
          },
          flexGrow: 1,
          height: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: `${tableHeight + 100}px`,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box className={classes.subContainer}>
            <Box sx={{ padding: "5px 30px", marginTop: "32px" }}>
              <h3 style={{ margin: 0 }}> Select a Category below for product pricelist </h3>
              {productCategories.map((catg, index) => (
                <Button
                  key={catg.value}
                  className={`btn ${classes.productCategoryBtn} ${
                    selectedProductCategory !== catg.value ? classes.unselectedCategory : ""
                  }`}
                  variant="contained"
                  style={{ fontSize: "14px", margin: "15px 30px 0 0", textTransform: "capitalize" }}
                  onClick={() => handleCategoryClick(catg.value)}
                >
                  {catg.label}
                </Button>
              ))}
            </Box>
            <Box sx={{ overflowY: "auto", maxHeight: `${tableHeight}px`, mt: 3 }}>
              <Grid container direction="column" className={classes.PanelContainer}>
                {filteredProducts.map((product, index) => (
                  <Grid item key={product.Product_id} sx={{ mb: 4 }}>
                    <Box className={classes.Panel}>
                      <Grid container direction="row">
                        <Grid item xs={12}>
                          <Box sx={{ display: "inline-flex", height: "100px" }}>
                            <Box>
                              <img
                                style={{ width: "80px", height: "80px", cursor: product.File ? "zoom-in" : "default" }}
                                crossOrigin="anonymous"
                                src={
                                  process.env.REACT_APP_API_URL +
                                  "/" +
                                  (product.File ? product.File : "file/products/NoImageAvailable.png")
                                }
                                alt="Product"
                                {...(product.File && { onClick: () => toggleZoom(index) })}
                              />

                              {product.File && zoomedIndex !== null && (
                                <Box className={classes.ZoomedImageDiv}>
                                  <img
                                    crossOrigin="anonymous"
                                    src={process.env.REACT_APP_API_URL + "/" + filteredProducts[zoomedIndex].File}
                                    alt="Product"
                                    className={classes.ZoomedImage}
                                    onClick={() => toggleZoom(zoomedIndex)}
                                  />
                                  <Box style={{ position: "absolute", top: 10, right: 10 }}>
                                    <CloseFullscreenIcon
                                      sx={{
                                        color: "#000",
                                        backgroundColor: "#fff",
                                        cursor: "pointer",
                                        borderRadius: "20px",
                                      }}
                                      fontSize="large"
                                      onClick={() => toggleZoom(zoomedIndex)}
                                    />
                                  </Box>
                                </Box>
                              )}
                            </Box>
                            <Box sx={{ display: "inline-flex", flexDirection: "column", paddingLeft: "20px" }}>
                              <h3 style={{ margin: 0, marginBottom: "10px" }}> {product.Name} </h3>
                              <div style={{ maxHeight: "50px", overflowY: "auto" }}> {product.Description} </div>
                            </Box>
                          </Box>
                          {productsPriceListData.length > 0 && (
                            <Box>
                              {productsPriceListData.filter((prdPrice) => prdPrice.Product_id == product.Product_id)
                                .length > 0 && (
                                <TableContainer style={{ overflowX: "auto" }}>
                                  <Table className={classes.table} size="small">
                                    <TableHead>
                                      <TableRow style={{ backgroundColor: "#dfe4e8" }}>
                                        <TableCell style={{ width: 250, position: "sticky" }}>Quantity</TableCell>
                                        {productsPriceListData
                                          .filter((prdPrice) => prdPrice.Product_id == product.Product_id)
                                          .map((prc) => (
                                            <TableCell key={prc.Product_id + "-" + prc.Quantity} style={{ width: 100 }}>
                                              {" "}
                                              {prc.Quantity}{" "}
                                            </TableCell>
                                          ))}
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      <TableRow>
                                        <TableCell style={{ position: "sticky" }}> {product.Description2} </TableCell>
                                        {productsPriceListData
                                          .filter((prdPrice) => prdPrice.Product_id == product.Product_id)
                                          .map((prc) => (
                                            <TableCell
                                              key={prc.Product_id + "-" + prc.Quantity + "-" + prc.Price}
                                              style={{ width: 100 }}
                                            >
                                              {" "}
                                              $ {prc.Price}{" "}
                                            </TableCell>
                                          ))}
                                      </TableRow>
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              )}
                            </Box>
                          )}
                        </Grid>
                      </Grid>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Box>
        )}
      </Box>
    </CommonLayout>
  );
};

export default Products;
